/**
 * ============================================
 * TFX - SISTEMA DE AUTENTICACIÓN CONSOLIDADO
 * ============================================
 * Versión: 2.3.0 - UX Modernizada con showAlert() + Logs Optimizados
 * 
 * Este archivo UNIFICA:
 * - auth-system.js (API de autenticación)
 * - auth-form.js (handlers de formularios)
 * - auth.js (lógica de registro/login)
 * - reset-password.js (recuperación de contraseña)
 * - session-timeout.js (timeout por inactividad)
 * 
 * CARACTERÍSTICAS:
 * ✅ Validación OBLIGATORIA de reCAPTCHA v2
 * ✅ stopImmediatePropagation para prevenir múltiples handlers
 * ✅ localStorage estandarizado con prefijo tfx_
 * ✅ Gestión centralizada de sesiones
 * ✅ Timeout automático por inactividad (15 min)
 * ✅ API unificada y consistente
 * ✅ Prevención de bypass de seguridad
 * ✅ Alertas modernas con showAlert() en lugar de alert()
 * ✅ Console logs optimizados (solo errores críticos y warnings importantes)
 * 
 * USO:
 * <script src="./js/auth-consolidated.js?v=2.3.0"></script>
 */

// VERIFICAR QUE NO SE CARGUE MÚLTIPLES VECES
if (window.__AUTH_CONSOLIDATED_LOADED__) {
} else {
    window.__AUTH_CONSOLIDATED_LOADED__ = true;
}

// ============================================
// CONFIGURACIÓN
// ============================================

const AUTH_API_BASE = 'https://trading-journal-preprod.monkeydfx-trader.workers.dev';

const AUTH_CONFIG = {
    API_BASE: AUTH_API_BASE,
    RECAPTCHA_SITE_KEY: '6Lf4ETssAAAAADT1s4Ho72bETMymUeqj6NXLWp4M',
    SESSION_TIMEOUT_MINUTES: 15,
    
    // localStorage Keys estandarizados
    KEYS: {
        TOKEN: 'tfx_token',
        EMAIL: 'tfx_email',
        USER_ID: 'tfx_userId',
        USERNAME: 'tfx_username',
        FULL_NAME: 'tfx_fullName',
        PLAN: 'tfx_plan',
        USER_TYPE: 'tfx_userType',
        STRIPE_ID: 'tfx_stripeId',
        PAYPAL_SUB_ID: 'tfx_paypalSubId',
        COUNTRY: 'tfx_country',
        CREATED_AT: 'tfx_createdAt',
        LAST_LOGIN: 'tfx_lastLogin',
        EXPIRES_AT: 'tfx_expiresAt',
        SESSION_ID: 'tfx_sessionId',
    }
};

try { window.AUTH_API_BASE = AUTH_CONFIG.API_BASE; } catch (e) {}

// ============================================
// MÓDULO: VALIDACIÓN DE reCAPTCHA
// ============================================

const CaptchaValidator = {
    /**
     * Valida que reCAPTCHA esté completo
     * @returns {Object} { valid: boolean, token: string|null, error: string|null }
     */
    validate(formType) {
        // 1. Verificar que grecaptcha esté disponible
        if (!window.grecaptcha) {
            return {
                valid: false,
                token: null,
                error: 'Sistema de verificación no disponible. Recarga la página.'
            };
        }

        let token = '';
        // Si es forgotForm, usar el token del campo oculto si existe
        if (formType === 'forgot') {
            const tokenField = document.getElementById('forgotPasswordCaptchaToken');
            if (tokenField && tokenField.value) {
                token = tokenField.value;
            } else {
                // fallback: intentar obtener el token del primer captcha
                try {
                    token = window.grecaptcha.getResponse();
                } catch (e) {
                    return {
                        valid: false,
                        token: null,
                        error: 'Error al verificar captcha. Recarga la página.'
                    };
                }
            }
        } else {
            // login/register: usar el captcha global
            try {
                token = window.grecaptcha.getResponse();
            } catch (e) {
                return {
                    valid: false,
                    token: null,
                    error: 'Error al verificar captcha. Recarga la página.'
                };
            }
        }

        // 3. Validación ESTRICTA: token debe existir y no estar vacío
        if (!token || typeof token !== 'string' || token.trim() === '') {
            return {
                valid: false,
                token: null,
                error: '⚠️ DEBES MARCAR el checkbox de verificación "No soy un robot"'
            };
        }

        // 4. Validación adicional: longitud mínima del token
        if (token.length < 20) {
            return {
                valid: false,
                token: null,
                error: 'Token de verificación inválido. Intenta de nuevo.'
            };
        }

        return {
            valid: true,
            token: token,
            error: null
        };
    },

    /**
     * Resetea el captcha
     */
    reset() {
        if (window.grecaptcha) {
            try {
                window.grecaptcha.reset();
            } catch (e) {
            }
        }
    }
};

// ============================================
// MÓDULO: GESTIÓN DE SESIONES
// ============================================

const SessionManager = {
    _timeoutId: null,
    _eventHandlers: [],

    /**
     * Inicia el temporizador de inactividad
     */
    startTimeout() {
        this.stopTimeout();

        const timeoutMs = AUTH_CONFIG.SESSION_TIMEOUT_MINUTES * 60 * 1000;
        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

        const resetTimer = () => {
            if (this._timeoutId) clearTimeout(this._timeoutId);
            
            this._timeoutId = setTimeout(() => {
                this.forceLogout('Sesión cerrada por inactividad');
            }, timeoutMs);
        };

        // Agregar listeners
        events.forEach(event => {
            window.addEventListener(event, resetTimer, { passive: true });
            this._eventHandlers.push({ event, handler: resetTimer });
        });

        // Iniciar timer
        resetTimer();
    },

    /**
     * Detiene el temporizador
     */
    stopTimeout() {
        if (this._timeoutId) {
            clearTimeout(this._timeoutId);
            this._timeoutId = null;
        }

        // Remover listeners
        this._eventHandlers.forEach(({ event, handler }) => {
            window.removeEventListener(event, handler);
        });
        this._eventHandlers = [];
    },

    /**
     * Forzar logout y limpiar sesión
     */
    forceLogout(reason = 'Sesión cerrada') {
        this.stopTimeout();
        StorageManager.clearAll();
        
        // FIX: No mostrar notificación al logout normal
        // Solo mostrar si es por inactividad o fuerza del servidor
        const isInactivity = reason.includes('inactividad');
        if (window.showAlert && isInactivity) {
            showAlert('Sesión cerrada', reason, 'warning');
        }

        // FIX: Marcar como logout voluntario para evitar mostrar 'sesión expirada' en index.html
        localStorage.setItem('_logoutVoluntario', 'true');
        
        setTimeout(() => {
            window.location.href = '/';
        }, 500); // Reducir a 500ms si no hay notificación
    },

    /**
     * Genera un ID único de sesión
     */
    generateSessionId() {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
};

// ============================================
// MÓDULO: GESTIÓN DE ALMACENAMIENTO
// ============================================

const StorageManager = {
    /**
     * Guarda los datos del usuario
     */
    saveUser(userData) {
        try {
            const sessionId = SessionManager.generateSessionId();
            
            sessionStorage.setItem(AUTH_CONFIG.KEYS.TOKEN, userData.token || '');
            sessionStorage.setItem(AUTH_CONFIG.KEYS.EMAIL, userData.email || '');
            sessionStorage.setItem(AUTH_CONFIG.KEYS.USER_ID, userData.id || '');
            localStorage.setItem(AUTH_CONFIG.KEYS.USERNAME, userData.username || '');
            localStorage.setItem(AUTH_CONFIG.KEYS.FULL_NAME, userData.full_name || '');
            localStorage.setItem(AUTH_CONFIG.KEYS.PLAN, userData.plan || 'free');
            localStorage.setItem(AUTH_CONFIG.KEYS.USER_TYPE, userData.user_type || 'user');
            localStorage.setItem(AUTH_CONFIG.KEYS.STRIPE_ID, userData.stripe_customer_id || '');
            localStorage.setItem(AUTH_CONFIG.KEYS.PAYPAL_SUB_ID, userData.paypal_subscription_id || '');
            localStorage.setItem(AUTH_CONFIG.KEYS.COUNTRY, userData.country || '');
            localStorage.setItem(AUTH_CONFIG.KEYS.CREATED_AT, userData.created_at || '');
            localStorage.setItem(AUTH_CONFIG.KEYS.LAST_LOGIN, new Date().toISOString());
            localStorage.setItem(AUTH_CONFIG.KEYS.EXPIRES_AT, userData.expires_at || '');
            localStorage.setItem(AUTH_CONFIG.KEYS.SESSION_ID, sessionId);

            // RETROCOMPATIBILIDAD: Guardar también las keys antiguas para páginas no actualizadas
            sessionStorage.setItem('userToken', userData.token || '');
            sessionStorage.setItem('authToken', userData.token || '');
            sessionStorage.setItem('currentUserId', userData.id || '');
            sessionStorage.setItem('userEmail', userData.email || '');
            sessionStorage.setItem('userPlan', userData.plan || 'free');
            sessionStorage.setItem('userType', userData.user_type || 'user');
            localStorage.setItem('userName', userData.username || '');
            localStorage.setItem('userPlan', userData.plan || 'free');
            localStorage.setItem('userType', userData.user_type || 'user');
            localStorage.setItem('stripeCustomerId', userData.stripe_customer_id || '');
            localStorage.setItem('paypalSubscriptionId', userData.paypal_subscription_id || '');
            localStorage.setItem('userCreatedAt', userData.created_at || '');
            localStorage.setItem('expiresAt', userData.expires_at || '');

            // Limpiar copias sensibles en localStorage
            localStorage.removeItem(AUTH_CONFIG.KEYS.TOKEN);
            localStorage.removeItem(AUTH_CONFIG.KEYS.EMAIL);
            localStorage.removeItem(AUTH_CONFIG.KEYS.USER_ID);
            localStorage.removeItem('userToken');
            localStorage.removeItem('authToken');
            localStorage.removeItem('currentUserId');
            localStorage.removeItem('userEmail');

            return true;
        } catch (e) {
            return false;
        }
    },

    /**
     * Obtiene los datos del usuario
     */
    getUser() {
        if (!this.isLoggedIn()) return null;

        return {
            token: sessionStorage.getItem(AUTH_CONFIG.KEYS.TOKEN) || localStorage.getItem(AUTH_CONFIG.KEYS.TOKEN),
            email: sessionStorage.getItem(AUTH_CONFIG.KEYS.EMAIL) || localStorage.getItem(AUTH_CONFIG.KEYS.EMAIL),
            userId: sessionStorage.getItem(AUTH_CONFIG.KEYS.USER_ID) || localStorage.getItem(AUTH_CONFIG.KEYS.USER_ID),
            username: localStorage.getItem(AUTH_CONFIG.KEYS.USERNAME),
            fullName: localStorage.getItem(AUTH_CONFIG.KEYS.FULL_NAME),
            plan: localStorage.getItem(AUTH_CONFIG.KEYS.PLAN),
            userType: localStorage.getItem(AUTH_CONFIG.KEYS.USER_TYPE),
            stripeId: localStorage.getItem(AUTH_CONFIG.KEYS.STRIPE_ID),
            paypalSubscriptionId: localStorage.getItem(AUTH_CONFIG.KEYS.PAYPAL_SUB_ID),
            country: localStorage.getItem(AUTH_CONFIG.KEYS.COUNTRY),
            createdAt: localStorage.getItem(AUTH_CONFIG.KEYS.CREATED_AT),
            lastLogin: localStorage.getItem(AUTH_CONFIG.KEYS.LAST_LOGIN),
            expiresAt: localStorage.getItem(AUTH_CONFIG.KEYS.EXPIRES_AT),
            sessionId: localStorage.getItem(AUTH_CONFIG.KEYS.SESSION_ID)
        };
    },

    /**
     * Verifica si hay sesión activa
     */
    isLoggedIn() {
        // Verificar primero las keys nuevas
        const token = sessionStorage.getItem(AUTH_CONFIG.KEYS.TOKEN) || localStorage.getItem(AUTH_CONFIG.KEYS.TOKEN);
        const email = sessionStorage.getItem(AUTH_CONFIG.KEYS.EMAIL) || localStorage.getItem(AUTH_CONFIG.KEYS.EMAIL);
        const userId = sessionStorage.getItem(AUTH_CONFIG.KEYS.USER_ID) || localStorage.getItem(AUTH_CONFIG.KEYS.USER_ID);
        
        if (token && email && userId) return true;
        
        // RETROCOMPATIBILIDAD: Verificar keys antiguas
        const oldToken = sessionStorage.getItem('userToken') || sessionStorage.getItem('authToken') ||
                 localStorage.getItem('userToken') || localStorage.getItem('authToken');
        const oldUserId = sessionStorage.getItem('currentUserId') || localStorage.getItem('currentUserId');
        
        return !!(oldToken && oldUserId);
    },

    /**
     * Limpia todos los datos de sesión
     */
    clearAll() {
        Object.values(AUTH_CONFIG.KEYS).forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
        try { sessionStorage.removeItem('subscriptionReminderOnLogin'); } catch (e) {}
        // Limpiar TODAS las keys antiguas también
        this._cleanupOldKeys();
    },

    /**
     * Limpia keys antiguos por compatibilidad
     */
    _cleanupOldKeys() {
        const oldKeys = [
            'authToken', 'userToken', 'userEmail', 'currentUserId',
            'userName', 'userPlan', 'userType', 'stripeCustomerId',
            'userCreatedAt', 'userLastLogin', 'expiresAt', 'userFullName',
            'userCountry', 'accountExpires', 'userExpiresAt'
        ];
        oldKeys.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
    },

    /**
     * Obtiene el plan del usuario
     */
    getPlan() {
        return (
            sessionStorage.getItem(AUTH_CONFIG.KEYS.PLAN) ||
            localStorage.getItem(AUTH_CONFIG.KEYS.PLAN) ||
            sessionStorage.getItem('userPlan') ||
            localStorage.getItem('userPlan') ||
            'free'
        );
    },

    /**
     * Verifica si el usuario es admin
     */
    isAdmin() {
        const userType = sessionStorage.getItem(AUTH_CONFIG.KEYS.USER_TYPE) ||
            localStorage.getItem(AUTH_CONFIG.KEYS.USER_TYPE) ||
            sessionStorage.getItem('userType') ||
            localStorage.getItem('userType');
        return String(userType || '').toLowerCase() === 'admin';
    }
};

// ============================================
// MÓDULO: API DE AUTENTICACIÓN
// ============================================

const AuthAPI = {
    /**
     * LOGIN con email/password + reCAPTCHA
     */
    async login(email, password, recaptchaToken) {
        try {
            // Validación básica
            if (!email || !password) {
                return { success: false, error: 'Email y contraseña requeridos' };
            }

            if (!recaptchaToken) {
                return { success: false, error: 'Token de reCAPTCHA requerido' };
            }

            const response = await fetch(`${AUTH_CONFIG.API_BASE}/api/auth/login-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: email.trim(),
                    password,
                    recaptchaToken
                })
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    error: data.error || 'Login fallido'
                };
            }

            if (!data.token || !data.user) {
                return {
                    success: false,
                    error: 'Respuesta inválida del servidor'
                };
            }

            // Guardar usuario
            const saved = StorageManager.saveUser({
                token: data.token,
                ...data.user
            });

            if (!saved) {
                return {
                    success: false,
                    error: 'Error al guardar sesión'
                };
            }

            // FIX: Limpiar flags de sesión expirada/logout voluntario al hacer login exitoso
            try { localStorage.removeItem('_sessionExpired'); } catch (e) {}
            try { localStorage.removeItem('_logoutVoluntario'); } catch (e) {}

            // Iniciar timeout de sesión
            SessionManager.startTimeout();

            return {
                success: true,
                user: data.user,
                message: 'Login exitoso'
            };

        } catch (error) {
            return {
                success: false,
                error: 'Error de conexión: ' + error.message
            };
        }
    },

    /**
     * LOGOUT
     */
    logout() {
        SessionManager.stopTimeout();
        StorageManager.clearAll();
        // FIX: Marcar como logout voluntario para evitar mostrar 'sesión expirada'
        localStorage.setItem('_logoutVoluntario', 'true');
        return { success: true, message: 'Sesión cerrada' };
    },

    /**
     * Iniciar registro (paso 1)
     */
    async initiateRegistration(email, username) {
        try {
            const response = await fetch(`${AUTH_CONFIG.API_BASE}/api/auth/initiate-registration`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, username, plan: 'free' })
            });

            const result = await response.json();

            if (!response.ok) {
                return { success: false, error: result.error || 'Error en el registro' };
            }

            return { success: true, registrationId: result.registrationId };
        } catch (error) {
            return { success: false, error: 'Error de conexión: ' + error.message };
        }
    },

    /**
     * Confirmar registro (paso 2)
     */
    async confirmRegistration(registrationId, tempPassword, newPassword, confirmPassword) {
        try {
            const response = await fetch(`${AUTH_CONFIG.API_BASE}/api/auth/confirm-registration`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ registrationId, tempPassword, newPassword, confirmPassword })
            });

            const result = await response.json();

            if (!response.ok) {
                return { success: false, error: result.error || 'Error al confirmar' };
            }

            // Guardar usuario
            if (result.token && result.user) {
                StorageManager.saveUser({
                    token: result.token,
                    ...result.user
                });
                SessionManager.startTimeout();
            }

            return { success: true, user: result.user };
        } catch (error) {
            return { success: false, error: 'Error de conexión: ' + error.message };
        }
    },

    /**
     * Olvidé mi contraseña
     */
    async forgotPassword(email, recaptchaToken) {
        try {
            if (!recaptchaToken) {
                return { success: false, error: 'reCAPTCHA requerido' };
            }

            const payload = { email, recaptchaToken };

            const response = await fetch(`${AUTH_CONFIG.API_BASE}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (!response.ok) {
                return { success: false, error: result.error || 'Error al enviar email' };
            }

            return { success: true, message: 'Email enviado' };
        } catch (error) {
            return { success: false, error: 'Error de conexión: ' + error.message };
        }
    },

    /**
     * Confirmar reset de contraseña
     */
    async resetPassword(token, newPassword) {
        try {
            const response = await fetch(`${AUTH_CONFIG.API_BASE}/api/reset-password-confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword })
            });

            const result = await response.json();

            if (!response.ok) {
                return { success: false, error: result.error || 'Error al resetear contraseña' };
            }

            return { success: true, message: 'Contraseña actualizada' };
        } catch (error) {
            return { success: false, error: 'Error de conexión: ' + error.message };
        }
    }
};

// ============================================
// API PÚBLICA UNIFICADA
// ============================================

window.AuthSystem = {
    // Autenticación
    login: (email, password, recaptchaToken) => AuthAPI.login(email, password, recaptchaToken),
    logout: () => AuthAPI.logout(),
    
    // Registro
    initiateRegistration: (email, username) => AuthAPI.initiateRegistration(email, username),
    confirmRegistration: (registrationId, tempPwd, newPwd, confirmPwd) => 
        AuthAPI.confirmRegistration(registrationId, tempPwd, newPwd, confirmPwd),
    
    // Recuperación de contraseña
    forgotPassword: (email, recaptchaToken) => AuthAPI.forgotPassword(email, recaptchaToken),
    resetPassword: (token, newPassword) => AuthAPI.resetPassword(token, newPassword),
    
    // Sesión
    getUser: () => StorageManager.getUser(),
    isLoggedIn: () => StorageManager.isLoggedIn(),
    getToken: () => sessionStorage.getItem(AUTH_CONFIG.KEYS.TOKEN) || localStorage.getItem(AUTH_CONFIG.KEYS.TOKEN),
    getPlan: () => StorageManager.getPlan(),
    isAdmin: () => StorageManager.isAdmin(),
    forceLogout: (reason) => SessionManager.forceLogout(reason),
    
    // Plan access
    hasPlanAccess: (requiredPlan) => {
        const userPlan = StorageManager.getPlan();
        const planHierarchy = { free: 0, pro: 1, unlimited: 2 };
        return (planHierarchy[userPlan] || 0) >= (planHierarchy[requiredPlan] || 0);
    },
    isPlan: (planName) => StorageManager.getPlan() === planName,
    
    // Captcha
    validateCaptcha: () => CaptchaValidator.validate(),
    resetCaptcha: () => CaptchaValidator.reset(),
    
    // Debug
    debugState: () => ({
        isLoggedIn: StorageManager.isLoggedIn(),
        user: StorageManager.getUser(),
        plan: StorageManager.getPlan(),
        isAdmin: StorageManager.isAdmin()
    })
};

// ============================================
// INICIALIZACIÓN AUTOMÁTICA
// ============================================

document.addEventListener('DOMContentLoaded', function() {
    // Si está logueado, iniciar timeout de sesión
    if (StorageManager.isLoggedIn()) {
        SessionManager.startTimeout();
    }

    // Configurar formularios si existen
    setupAuthForms();
});

// ============================================
// CONFIGURACIÓN DE FORMULARIOS
// ============================================

function setupAuthForms() {
    // PASSWORD TOGGLES (no dependen de reCAPTCHA)
    document.querySelectorAll('.password-toggle').forEach(btn => {
        btn.addEventListener('click', function() {
            const input = document.getElementById(this.dataset.target);
            if (input) {
                input.type = input.type === 'password' ? 'text' : 'password';
            }
        });
    });

    // LOGIN FORM - CONFIGURACIÓN ROBUSTA SIN CLONADO
    const loginForm = document.getElementById('loginForm');
    if (loginForm) {
        // Crear una función handler única
        const handleLoginSubmit = async (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation(); // IMPORTANTE: Detener otros handlers


            const emailInput = document.getElementById('loginEmail');
            const passwordInput = document.getElementById('loginPassword');
            
            if (!emailInput || !passwordInput) {
                showAlert('Error de formulario', 'Por favor recarga la página.', 'error');
                return false;
            }

            const email = emailInput.value.trim();
            const password = passwordInput.value;

            if (!email || !password) {
                showAlert('Campos requeridos', 'Por favor completa email y contraseña', 'warning');
                return false;
            }

            // VALIDACIÓN CRÍTICA Y OBLIGATORIA: reCAPTCHA
            const captchaValidation = CaptchaValidator.validate();
            
            if (!captchaValidation.valid) {
                showAlert('Verificación requerida', captchaValidation.error, 'warning');
                CaptchaValidator.reset();
                return false; // BLOQUEAR COMPLETAMENTE
            }

            // Deshabilitar botón para evitar doble submit
            const submitBtn = loginForm.querySelector('button[type="submit"]');
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.textContent = 'Procesando...';
            }

            // Ejecutar login
            try {
                const result = await AuthAPI.login(email, password, captchaValidation.token);
                
                if (result.success) {
                    // SIN notificación de éxito - redirige directamente al journal
                    try { sessionStorage.setItem('subscriptionReminderOnLogin', '1'); } catch (e) {}
                    
                    // Disparar notificación de bienvenida
                    setTimeout(() => {
                        if (window.NotificationCenter && window.NotificationCenter.checkFirstLoginOfDay) {
                            window.NotificationCenter.checkFirstLoginOfDay();
                        }
                        window.location.href = './journal';
                    }, 500);
                } else {
                    showAlert('Error de autenticación', result.error, 'error');
                    CaptchaValidator.reset();
                    if (submitBtn) {
                        submitBtn.disabled = false;
                        submitBtn.textContent = 'Entrar';
                    }
                }
            } catch (error) {
                showAlert('Error de conexión', error.message, 'error');
                CaptchaValidator.reset();
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.textContent = 'Entrar';
                }
            }
            
            return false;
        };
        
        // Remover listeners previos si existen
        loginForm.removeEventListener('submit', handleLoginSubmit);
        
        // Agregar el nuevo listener
        loginForm.addEventListener('submit', handleLoginSubmit, { once: false, capture: true });
    }


    // FORGOT PASSWORD FORM
    const forgotForm = document.getElementById('forgotForm');
    if (forgotForm) {
        forgotForm.addEventListener('submit', async (e) => {
            e.preventDefault();

            const email = document.getElementById('forgotEmail')?.value.trim();
            
            if (!email) {
                showAlert('Campo requerido', 'Por favor ingresa tu email', 'warning');
                return;
            }

            // Obtener token del captcha específico del formulario forgot
            let token = '';
            try {
                // Buscar el captcha dentro del forgotForm
                const captchaDiv = forgotForm.querySelector('.g-recaptcha');
                
                if (captchaDiv && window.grecaptcha) {
                    // Intenta obtener usando data-widget-id si existe
                    const widgetId = captchaDiv.getAttribute('data-widget-id');
                    
                    if (widgetId !== null && !Number.isNaN(widgetId)) {
                        token = window.grecaptcha.getResponse(parseInt(widgetId));
                    } else {
                        // Fallback: obtener el token del campo oculto
                        const tokenField = document.getElementById('forgotPasswordCaptchaToken');
                        
                        if (tokenField && tokenField.value) {
                            token = tokenField.value;
                        }
                    }
                }
            } catch (e) {
            }

            if (!token || token.trim() === '') {
                showAlert('Verificación requerida', '⚠️ DEBES MARCAR el checkbox de verificación "No soy un robot"', 'warning');
                return;
            }

            try {
                const result = await AuthAPI.forgotPassword(email, token);
                
                if (result.success) {
                    showAlert('Email enviado', 'Revisa tu inbox para restablecer tu contraseña', 'success');
                    if (window.switchForm) window.switchForm('login');
                    forgotForm.reset();
                } else {
                    showAlert('Error', result.error, 'error');
                }
            } catch (error) {
                showAlert('Error de conexión', error.message, 'error');
            }
            
            // Resetear el captcha específico
            if (window.grecaptcha) {
                try {
                    const captchaDiv = forgotForm.querySelector('.g-recaptcha');
                    if (captchaDiv) {
                        const widgetId = captchaDiv.getAttribute('data-widget-id');
                        if (widgetId !== null && !Number.isNaN(widgetId)) {
                            window.grecaptcha.reset(parseInt(widgetId));
                        }
                    }
                } catch (e) {
                }
            }
        });
    }

    // Esperar a que grecaptcha esté disponible (sin bloquear eventos)
    function waitForCaptcha(callback) {
        if (window.grecaptcha) {
            callback();
        } else {
            setTimeout(() => waitForCaptcha(callback), 100);
        }
    }

    waitForCaptcha(() => {});
}

// ============================================
// FUNCIONES GLOBALES AUXILIARES
// ============================================

window.switchTab = function(tabName) {
    // Ocultar todos los tabs y formularios
    document.querySelectorAll('.tab-content').forEach(el => {
        el.style.display = 'none';
        el.classList.remove('active');
    });
    document.querySelectorAll('form[data-tab]').forEach(el => {
        el.style.display = 'none';
        el.classList.remove('active');
    });
    document.querySelectorAll('[data-tab]').forEach(el => {
        el.classList.remove('active');
    });

    // Mostrar tab seleccionado
    const contentTab = document.querySelector(`.tab-content[data-tab="${tabName}"]`);
    if (contentTab) {
        contentTab.style.display = 'block';
        contentTab.classList.add('active');
    }
    // Mostrar formulario correspondiente
    const formTab = document.getElementById(tabName + 'Form');
    if (formTab) {
        formTab.style.display = 'block';
        formTab.classList.add('active');
    }

    // Activar botón del tab
    const tabButton = document.getElementById(tabName + 'Tab');
    if (tabButton) {
        tabButton.classList.add('active');
    }
};

window.switchForm = function(formType) {
    const forms = ['loginForm', 'forgotForm', 'registerForm'];
    forms.forEach(formId => {
        const form = document.getElementById(formId);
        if (form) {
            form.style.display = formId === (formType + 'Form') ? 'block' : 'none';
        }
    });
    // Si cambiamos a forgotForm, resetear el captcha
    if (formType === 'forgot' && window.grecaptcha && typeof window.grecaptcha.reset === 'function') {
        // Buscar el primer captcha dentro del forgotForm
        const forgotForm = document.getElementById('forgotForm');
        if (forgotForm) {
            const captchaDiv = forgotForm.querySelector('.g-recaptcha');
            if (captchaDiv && !captchaDiv.getAttribute('data-widget-id') && typeof window.grecaptcha.render === 'function') {
                try {
                    const sitekey = captchaDiv.getAttribute('data-sitekey');
                    if (sitekey) {
                        const widgetId = window.grecaptcha.render(captchaDiv, {
                            sitekey,
                            callback: window.onForgotPasswordCaptchaSuccess || undefined,
                            'expired-callback': window.onForgotPasswordCaptchaExpired || undefined
                        });
                        if (widgetId !== undefined && widgetId !== null) {
                            captchaDiv.setAttribute('data-widget-id', String(widgetId));
                        }
                    }
                } catch (e) {}
            }
            if (captchaDiv && captchaDiv.getAttribute('data-widget-id')) {
                window.grecaptcha.reset(captchaDiv.getAttribute('data-widget-id'));
            } else {
                // fallback: resetear todos los captchas
                window.grecaptcha.reset();
            }
        }
        // Limpiar el campo hidden del token
        const tokenField = document.getElementById('forgotPasswordCaptchaToken');
        if (tokenField) tokenField.value = '';
    }
};

// Funciones de registro
let currentRegistrationId = null;

window.registerStep2 = async function() {
    const email = document.getElementById('regEmail')?.value.trim();
    const username = document.getElementById('regUsername')?.value.trim();

    if (!email || !username) {
        showAlert('Campos requeridos', 'Por favor completa todos los campos', 'warning');
        return;
    }

    try {
        const result = await AuthAPI.initiateRegistration(email, username);
        
        if (result.success) {
            currentRegistrationId = result.registrationId;
            showAlert('Email enviado', 'Revisa tu inbox para la contraseña temporal', 'success');
            
            document.getElementById('registerStep1')?.classList.add('hidden');
            document.getElementById('registerStep2')?.classList.remove('hidden');
        } else {
            showAlert('Error en el registro', result.error, 'error');
        }
    } catch (error) {
        showAlert('Error de conexión', error.message, 'error');
    }
};

window.registerBackToStep1 = function() {
    document.getElementById('registerStep1')?.classList.remove('hidden');
    document.getElementById('registerStep2')?.classList.add('hidden');
};

window.submitRegistration = async function() {
    const tempPwd = document.getElementById('regTempPassword')?.value;
    const newPwd = document.getElementById('regPassword')?.value;
    const confirmPwd = document.getElementById('regConfirmPassword')?.value;

    if (!tempPwd || !newPwd || !confirmPwd) {
        showAlert('Campos requeridos', 'Por favor completa todos los campos', 'warning');
        return;
    }

    if (newPwd !== confirmPwd) {
        showAlert('Error de validación', 'Las contraseñas no coinciden', 'error');
        return;
    }

    if (newPwd.length < 8) {
        showAlert('Contraseña débil', 'La contraseña debe tener mínimo 8 caracteres', 'warning');
        return;
    }

    try {
        const result = await AuthAPI.confirmRegistration(currentRegistrationId, tempPwd, newPwd, confirmPwd);
        
        if (result.success) {
            const hasSession = (typeof StorageManager !== 'undefined' && StorageManager.isLoggedIn && StorageManager.isLoggedIn());
            if (hasSession) {
                try { sessionStorage.setItem('subscriptionReminderOnLogin', '1'); } catch (e) {}
                showAlert('Cuenta creada', 'Redirigiendo a tu panel...', 'success');
                setTimeout(() => {
                    window.location.href = './journal';
                }, 1500);
            } else {
                showAlert('Cuenta creada', 'Ahora inicia sesión con tu nueva contraseña.', 'success');
                try {
                    switchTab('login');
                    const emailEl = document.getElementById('loginEmail');
                    if (emailEl) {
                        const regEmail = document.getElementById('regEmail')?.value || '';
                        if (regEmail) emailEl.value = regEmail;
                    }
                } catch (e) {}
            }
        } else {
            showAlert('Error al confirmar', result.error, 'error');
        }
    } catch (error) {
        showAlert('Error de conexión', error.message, 'error');
    }
};

// Nota: window.onForgotPasswordCaptchaSuccess se define en auth-redesigned.html ANTES de cargar este script
// para asegurar que reCAPTCHA lo encuentre cuando renderice el widget con data-callback
